import { LitElement, html, nothing } from 'lit';
import { t } from '../i18n';
import { subscribeAppNav, type AppNavState, type MainTab } from '../app-nav';
import { isSettingsHash } from '../settings-nav-logic';
import {
  loadRuntimeCapabilities,
  subscribeRuntimeCapabilities,
  type RuntimeCapabilitySnapshot,
} from '../runtime-capabilities';
import { defineZenComponent } from './register';

export type AppRailAction = { action: 'tab'; tab: MainTab } | { action: 'logout' };

type RailTab = { tab: MainTab; icon: string; labelKey: string };

// #2856: 今日, すべて and 完了 sit one step in under 「ゴール」; it is a heading, not a toggle.
const GOAL_TABS: RailTab[] = [
  { tab: 'today', icon: 'today', labelKey: 'tabToday' },
  { tab: 'all', icon: 'view_list', labelKey: 'tabAll' },
  { tab: 'done', icon: 'check_circle', labelKey: 'tabDone' },
];
const CREATE_TAB: RailTab = { tab: 'create', icon: 'add_circle', labelKey: 'appRailCreate' };

/**
 * #2749: the left menu on a wide screen. The work comes first, and settings
 * and the account sit at the bottom, out of the way of the day's work.
 */
export class ZenAppRail extends LitElement {
  static properties = {
    nav: { state: true },
    inSettings: { state: true },
    accountOpen: { state: true },
    workspaceEnabled: { state: true },
  };

  declare nav: AppNavState;
  declare inSettings: boolean;
  declare accountOpen: boolean;
  declare workspaceEnabled: boolean;

  private unsubscribeNav: (() => void) | null = null;
  private unsubscribeCapabilities: (() => void) | null = null;

  constructor() {
    super();
    this.nav = { tab: 'today', name: '' };
    this.inSettings = isSettingsHash(location.hash);
    this.accountOpen = false;
    this.workspaceEnabled = false;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribeNav = subscribeAppNav((nav) => { this.nav = nav; });
    this.unsubscribeCapabilities = subscribeRuntimeCapabilities(this.onCapabilitiesChanged);
    window.addEventListener('hashchange', this.onHashChange);
    document.addEventListener('zen-lang-change', this.onLangChange);
    document.addEventListener('click', this.onDocumentClick);
    void loadRuntimeCapabilities();
  }

  disconnectedCallback() {
    this.unsubscribeNav?.();
    this.unsubscribeNav = null;
    this.unsubscribeCapabilities?.();
    this.unsubscribeCapabilities = null;
    window.removeEventListener('hashchange', this.onHashChange);
    document.removeEventListener('zen-lang-change', this.onLangChange);
    document.removeEventListener('click', this.onDocumentClick);
    super.disconnectedCallback();
  }

  private onCapabilitiesChanged = (snapshot: RuntimeCapabilitySnapshot) => {
    this.workspaceEnabled = snapshot.status === 'ready' && snapshot.capabilities.workspace_admin === true;
  };

  private onHashChange = () => {
    this.inSettings = isSettingsHash(location.hash);
    this.accountOpen = false;
  };

  private onLangChange = () => this.requestUpdate();

  private onDocumentClick = (event: Event) => {
    if (!this.accountOpen) return;
    const wrap = this.querySelector('.app-rail-account');
    if (wrap && !wrap.contains(event.target as Node)) this.accountOpen = false;
  };

  private emit(detail: AppRailAction) {
    this.dispatchEvent(new CustomEvent<AppRailAction>('app-rail-action', { detail, bubbles: true, composed: true }));
  }

  private toggleAccount() {
    this.accountOpen = !this.accountOpen;
    if (this.accountOpen) {
      void this.updateComplete.then(() => this.querySelector<HTMLElement>('.app-rail-menu button')?.focus());
    }
  }

  private closeAccount(returnFocus: boolean) {
    this.accountOpen = false;
    if (returnFocus) this.querySelector<HTMLElement>('.app-rail-account-button')?.focus();
  }

  private openWorkspace() {
    this.accountOpen = false;
    location.hash = '#settings-members';
  }

  private renderTab({ tab, icon, labelKey }: RailTab, nested: boolean) {
    const current = !this.inSettings && this.nav.tab === tab;
    return html`<button
      type="button"
      class="app-rail-item ${nested ? 'app-rail-sub' : ''} ${current ? 'active' : ''}"
      aria-current=${current ? 'page' : nothing}
      data-rail-tab=${tab}
      @click=${() => this.emit({ action: 'tab', tab })}
    >
      <span class="mi" aria-hidden="true">${icon}</span>
      <span class="app-rail-label">${t(labelKey)}</span>
    </button>`;
  }

  render() {
    const { nav } = this;
    const account = t('appRailAccount') as string;
    return html`
      <nav class="app-rail" aria-label=${t('appRailLabel')}>
        <div class="app-rail-main">
          <div class="app-rail-group" role="group" aria-labelledby="appRailGoals">
            <div class="app-rail-heading" id="appRailGoals">
              <span class="mi" aria-hidden="true">flag</span>
              <span class="app-rail-label">${t('appRailGoals')}</span>
            </div>
            ${GOAL_TABS.map((tab) => this.renderTab(tab, true))}
          </div>
          ${this.renderTab(CREATE_TAB, false)}
        </div>
        <div class="app-rail-foot">
          <a
            class="app-rail-item ${this.inSettings ? 'active' : ''}"
            href="#settings"
            aria-current=${this.inSettings ? 'page' : nothing}
          >
            <span class="mi" aria-hidden="true">settings</span>
            <span class="app-rail-label">${t('settingsTitle')}</span>
          </a>
          <div class="app-rail-account" @keydown=${(e: KeyboardEvent) => { if (e.key === 'Escape' && this.accountOpen) { e.stopPropagation(); this.closeAccount(true); } }}>
            <button
              type="button"
              class="app-rail-item app-rail-account-button"
              aria-haspopup="true"
              aria-expanded=${this.accountOpen ? 'true' : 'false'}
              aria-controls="appRailAccountMenu"
              title=${account}
              @click=${() => this.toggleAccount()}
            >
              <span class="mi" aria-hidden="true">account_circle</span>
              <span class="app-rail-label">${nav.name || account}</span>
              <span class="mi mi-sm app-rail-account-chevron" aria-hidden="true">${this.accountOpen ? 'expand_more' : 'expand_less'}</span>
            </button>
            <div id="appRailAccountMenu" class="app-rail-menu" ?hidden=${!this.accountOpen}>
              ${this.workspaceEnabled ? html`<button type="button" @click=${() => this.openWorkspace()}>
                <span class="mi mi-sm" aria-hidden="true">groups</span>${t('workspaceMenuItem')}
              </button>` : nothing}
              <button type="button" @click=${() => { this.closeAccount(false); this.emit({ action: 'logout' }); }}>
                <span class="mi mi-sm" aria-hidden="true">logout</span>${t('logout')}
              </button>
            </div>
          </div>
        </div>
      </nav>
    `;
  }
}

defineZenComponent('zen-app-rail', ZenAppRail);
